import { alpha } from '@mui/material/styles';
import { Card, CardContent, Box, Typography, Skeleton, Stack, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import type { StatCardProperties } from '@/types/application/common/components/cards/stat-card';

export function StatCard({
  title,
  value,
  subtitle,
  icon,
  color = '#23395d',
  accentBorder,
  onClick,
  description,
}: StatCardProperties) {
  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        ...(accentBorder
          ? {
              border: '2px solid',
              borderColor: accentBorder,
              boxShadow: `0 0 0 4px ${alpha(accentBorder, 0.22)}`,
            }
          : {}),
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: accentBorder
            ? `0 6px 20px ${alpha(accentBorder, 0.35)}`
            : '0 4px 12px rgba(0,0,0,0.1)',
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                {title}
              </Typography>
              {description && (
                <Tooltip 
                  title={description} 
                  arrow 
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 400,
                        fontSize: '0.75rem',
                        lineHeight: 1.5,
                        bgcolor: 'rgba(0, 0, 0, 0.9)',
                      },
                    },
                  }}
                >
                  <InfoOutlined 
                    sx={{ 
                      fontSize: 14, 
                      color: 'text.secondary',
                      cursor: 'help',
                      '&:hover': { color: 'primary.main' },
                    }} 
                  />
                </Tooltip>
              )}
            </Box>
            <Typography variant="h4" sx={{ mt: 0.5, color, fontWeight: 700 }}>
              {value}
            </Typography>
            {subtitle != null &&
              subtitle !== '' &&
              (typeof subtitle === 'string' ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {subtitle}
                </Typography>
              ) : (
                <Box sx={{ mt: 0.5 }}>{subtitle}</Box>
              ))}
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: `${color}10`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function StatCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: 2.5 }}>
        <Skeleton width={80} height={14} />
        <Skeleton width={60} height={36} sx={{ mt: 0.5 }} />
        <Skeleton width={120} height={14} sx={{ mt: 0.5 }} />
      </CardContent>
    </Card>
  );
}

export default StatCard;

